/**
 * The feed on Now, pure: what just happened, newest first, in plain words built from the documents' own
 * info texts — one line per run ("SO-763688 split into 4 sales lines for the data warehouse", "Purchase
 * order PO-1063700 created in ERP as SO-763700 · Lahden Rakennusliike Oy") — with the ongoing things
 * pinned on top: an integration that keeps failing, one that has gone quiet, a schedule that just ran on
 * time. Identifiers come out as their own parts so the page sets them in mono and links the subject.
 * No React, no DOM: tested with node --test.
 */
import type { Activity, ActivityDoc, Flow, Hit } from '../lib/api.ts';
import { article, count, duration } from '../lib/format.ts';
import { stripSystem, systemName, systemOf } from '../lib/systems.ts';
import { noun } from '../lib/nouns.ts';
import { incidentWhat, incidentsOf, rhythmOfFlow, targetOf, type Incident } from './model.ts';

/** A stored message by where it is (the same shape as the peeks' MessageRef). */
export interface MessageRef { vaultId: string; uuid: string; day?: string }

export type FeedPart = string | { mono: string; subject?: boolean };
export type FeedOpen = { kind: 'document'; ref: MessageRef } | { kind: 'search'; q: string };

export interface FeedLine {
  key: string;
  /** When it happened; a pinned line about something ongoing carries `now`. */
  at: number;
  pinned: boolean;
  kind: 'run' | 'document' | 'record';
  tone: 'fail' | 'warn' | 'ok' | null;
  parts: FeedPart[];
  /** The identifier the line is about, for the link into the stage. */
  subject: string | null;
  open: FeedOpen;
  /** The integration the line names, and the words of the line that name it (they open its peek). */
  about?: FeedAbout;
}

/** An integration a feed line names: `phrase` is the text of the line that says it. */
export interface FeedAbout { kind: 'integration'; process: string; phrase: string }

export interface FeedInput {
  activity: Activity;
  /**
   * The newest failed documents of the window, newest first (a search for @status=FAILED): the words of the
   * streaks in `activity.failing` (which the page fills in itself for a cloud without them) and "failed again".
   */
  failed: Hit[];
  now: number;
}

/** A schedule counts as "just ran" this long after its run. */
const JUST_RAN = 15 * 60_000;

// ---------------------------------------------------------------- words

const ID = /\b([A-Za-z]{1,5}-\d{2,}(?:-\d+)?|JJFI\d{6,}|\d{6,})\b/g;
/** A table or enum name as the data writes it: SALES_LINE, IN_TRANSIT. */
const TOKEN = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g;
/** The status words systems write in capitals, said in lower case. */
const STATUS_WORDS = /\b(?:DELIVERED|SHIPPED|RECEIVED|CONFIRMED|CANCELLED|CANCELED|RETURNED|COMPLETED|CREATED|UPDATED|DELETED|ACCEPTED|REJECTED|PENDING|PROCESSING|EXCEPTION|SUCCESS|FAILED|HOLD)\b/g;
const ID_ONE = new RegExp(ID.source);
const RECORD = /Customer|Account|Employee|Product|Item|Contact|Rate|Supplier|Vendor|Meter\b/i;
const SYSTEM_WORDS = /^(ERP|WMS|CRM|EDI|SFTP|HR|DWH|FIN|SALES_LINE|Salesforce|webshop|the webshop|Maventa|Posti)$/i;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** "so-763649" as the data writes it elsewhere: "SO-763649". */
const normalId = (id: string) => (/^[a-z]{1,5}-\d/.test(id) ? id.toUpperCase() : id);

/** The identifiers in a text, as written. */
export function idsIn(text: string): string[] {
  return [...text.matchAll(ID)].map((m) => normalId(m[1]));
}

/** The identifier a text is about: the first one with a prefix (SO-1, CUST-1), else the first at all. */
export function subjectOf(text: string): string | null {
  const ids = idsIn(text);
  return ids.find((id) => /^[A-Z]+-\d/.test(id)) ?? ids[0] ?? null;
}

/** The text split into words and identifiers; the subject's parts are marked. */
export function partsOf(text: string, subject: string | null): FeedPart[] {
  const parts: FeedPart[] = [];
  let last = 0;
  for (const m of text.matchAll(ID)) {
    const i = m.index ?? 0;
    if (i > last) parts.push(text.slice(last, i));
    const id = normalId(m[1]);
    parts.push(subject && id === subject ? { mono: id, subject: true } : { mono: id });
    last = i + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** A company named in an info text ("received from Lahden Rakennusliike Oy", "sent to Kokkolan Kattotalo"). */
export function nameIn(info: string | null | undefined): string | null {
  if (!info) return null;
  const m = /\b(?:from|to|of)\s+([A-ZÅÄÖ][\wåäö&-]*(?:\s+[A-ZÅÄÖa-zåäö][\wåäö&.-]*){1,4})\s*$/.exec(info.replace(/\s*\([^)]*\)\s*$/, ''));
  const name = m?.[1]?.trim();
  if (!name || SYSTEM_WORDS.test(name) || !/[a-zåäö]/.test(name) || ID_ONE.test(name)) return null;
  return name;
}

/** "the data warehouse as a sales line": where a document went and what it is there, for a table name the data writes instead. */
export function storedAs(doc: Pick<ActivityDoc, 'cls' | 'system'>, process: string | null): string {
  const what = noun(doc.cls);
  const where = (process && targetOf(process)) ?? (doc.system ? systemName(doc.system) : null);
  return where ? `${where} as ${article(what)} ${what}` : `${article(what)} ${what}`;
}

/** A word of capitals as words: SALES_LINE → sales line, IN_TRANSIT → in transit. */
const tokenWords = (t: string) => t.toLowerCase().replace(/_/g, ' ');

/**
 * An info text in the feed's voice: file names dropped, a failure said as "… failed · <reason>", a hold as
 * "… on hold · <why>", a parenthesis as a clause, and never a raw token — a table the document was loaded
 * to is said as where it went and what it is there (`stored`, from storedAs), other codes become words,
 * status words lose their capitals, "upsert" is "update".
 */
export function voice(info: string, status: string | null | undefined, stored?: string): string {
  let t = info.trim().replace(/\s+/g, ' ');
  t = t.replace(/:\s*[\w.-]+\.(?:xml|json|csv|txt|edi|edifact)\b.*$/i, '');
  if (stored) t = t.replace(/\b(to|into|in)\s+[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/, (_m, prep: string) => `${prep} ${stored}`);
  t = t.replace(TOKEN, tokenWords).replace(STATUS_WORDS, (w) => w.toLowerCase());
  t = t.replace(/\bupserted to\b/gi, 'updated in').replace(/\bupsert(ed|s)?\b/gi, 'update$1');
  const at = status === 'FAILED' ? /\bfailed\b/i.exec(t) : null;
  if (at) {
    // the reason keeps its own parentheses as a clause: "Maventa 401, token expired"
    const head = t.slice(0, at.index).trim().replace(/[:,;-]+$/, '');
    const reason = reasonText(t.slice(at.index + at[0].length));
    t = reason ? `${head} failed · ${reason}` : `${head} failed`;
  } else {
    t = t.replace(/\s*\(([^()]+)\)\s*$/, ' · $1');
    if (status === 'FAILED') t = `${t} · failed`;
    else if (status === 'HOLD') {
      const i = t.indexOf(': ');
      t = i > 0 ? `${t.slice(0, i)} on hold · ${t.slice(i + 2)}` : `${t.replace(/\s+HOLD$/i, '')} · on hold`;
    }
  }
  t = t.replace(/\s*:\s+/g, ' · ');
  return cap(t);
}

const STATUS_TEXT = /\b(\d{3})\s+(?:Unauthorized|Forbidden|Not Found|Conflict|Bad Request|Internal Server Error|Bad Gateway|Service Unavailable|Gateway Timeout|Too Many Requests|Unprocessable Entity|Method Not Allowed)\b/gi;
const reasonText = (rest: string) =>
  rest.replace(/^[:\s—–-]+/, '').replace(STATUS_TEXT, '$1').replace(/\s*\(([^()]+)\)/g, ', $1').replace(/\s*:\s+/g, ', ').trim().replace(/[.,;]+$/, '');

// ---------------------------------------------------------------- lines from the documents

const isRecord = (cls: string) => RECORD.test(cls);
const ref = (d: ActivityDoc): MessageRef => (d.day ? { vaultId: d.vaultId, uuid: d.uuid, day: d.day } : { vaultId: d.vaultId, uuid: d.uuid });
const searchOpen = (process: string): FeedOpen => ({ kind: 'search', q: `@process="${process}"` });

/** The documents of one run as one line. */
export function lineOfRun(docs: ActivityDoc[], failed: Hit[]): FeedLine {
  const sorted = [...docs].sort((a, b) => a.at - b.at);
  const out = [...sorted].reverse().find((d) => d.status && d.status !== 'NEW') ?? sorted[sorted.length - 1];
  const status = out.status;
  const tone = status === 'FAILED' ? 'fail' : status === 'HOLD' ? 'warn' : null;
  const process = out.process ?? sorted.find((d) => d.process)?.process ?? null;
  let text: string;
  let subject: string | null;

  // One thing became many lines: say that, not every line
  const lines = sorted.filter((d) => /^Line \d+ of \S+\b/i.test(d.info ?? ''));
  if (lines.length >= 2 && tone === null) {
    const first = lines[0].info ?? '';
    subject = subjectOf(first.replace(/^Line \d+ of\s+/i, ''));
    const target = process ? targetOf(process) : null;
    text = `${subject ?? cap(noun(lines[0].cls))} split into ${count(lines.length)} ${noun(lines[0].cls, lines.length)}${target ? ` for ${target}` : ''}`;
  } else if (out.info) {
    text = voice(out.info, status, storedAs(out, process));
    subject = subjectOf(text);
    // "… failed again" when the same thing failed before in this integration
    const s = subject;
    if (status === 'FAILED' && s && failed.some((h) => h.uuid !== out.uuid && h.timestamp < out.at && h.meta.process === process && idsIn(h.meta.info ?? '').includes(s))) {
      text = text.replace(/ failed( ·|$)/, ' failed again$1');
    }
    // The company named on the way in, when the line does not say it
    const name = sorted.map((d) => nameIn(d.info)).find((n) => n && !text.includes(n));
    if (name) text = `${text} · ${name}`;
  } else {
    // A document without an info text: the activity knows no identifier of it, only what it is and who stored it
    subject = null;
    text = `${cap(noun(out.cls))} stored by ${process ? stripSystem(process) : 'an integration'}`;
  }
  const about: FeedAbout | undefined = process && text.includes(stripSystem(process)) && stripSystem(process) ? { kind: 'integration', process, phrase: stripSystem(process) } : undefined;

  const kind = isRecord(out.cls) ? 'record' : tone !== null || sorted.length >= 3 ? 'run' : 'document';
  return {
    key: out.executionId ?? out.uuid, at: out.at, pinned: false, kind, tone, parts: partsOf(text, subject), subject, open: { kind: 'document', ref: ref(out) },
    ...(about && { about }),
  };
}

/** The newest documents grouped by run (a document without a run is its own group), newest first. */
export function runsOf(latest: ActivityDoc[]): ActivityDoc[][] {
  const groups = new Map<string, ActivityDoc[]>();
  for (const d of latest) {
    const k = d.executionId ?? d.uuid;
    const g = groups.get(k);
    if (g) g.push(d);
    else groups.set(k, [d]);
  }
  return [...groups.values()].sort((a, b) => Math.max(...b.map((d) => d.at)) - Math.max(...a.map((d) => d.at)));
}

// ---------------------------------------------------------------- the ongoing things, pinned

function failingLine(f: Incident, flow: Flow | undefined, systemOfCls: Map<string, string>, now: number): FeedLine {
  const what = cap(incidentWhat(f, flow, systemOfCls));
  const text = `${what} have failed for ${duration(Math.max(60_000, now - f.since))}${f.reason ? ` — ${f.reason}` : ''} · ${count(f.count)} ${flow ? noun(flow.from, f.count) : 'documents'} waiting`;
  return {
    key: `failing:${f.process}`, at: now, pinned: true, kind: 'run', tone: 'fail', parts: partsOf(text, null), subject: null,
    open: { kind: 'document', ref: f.newestRef }, about: { kind: 'integration', process: f.process, phrase: what },
  };
}

function silentLine(process: string, last: number, usualMs: number, flow: Flow | undefined, now: number): FeedLine {
  const system = flow?.system ?? systemOf(process) ?? '';
  const what = flow ? noun(flow.from, 2) : stripSystem(process).toLowerCase();
  const text = `${systemName(system)} silent for ${duration(Math.max(60_000, now - last))} · ${what} usually every ${duration(usualMs)}`;
  return {
    key: `silent:${process}`, at: now, pinned: true, kind: 'run', tone: 'warn', parts: partsOf(text, null), subject: null, open: searchOpen(process),
    about: { kind: 'integration', process, phrase: systemName(system) },
  };
}

function onTimeLine(flow: Flow, kindWord: string, run: ActivityDoc[] | undefined): FeedLine {
  let shape = '';
  if (run) {
    const ins = run.filter((d) => d.cls === flow.from).length;
    const outs = run.filter((d) => flow.to.includes(d.cls)).length;
    const to = flow.to[0];
    if (to && ins >= 2 && outs === 1) shape = ` · ${count(ins)} ${noun(flow.from, ins)} → 1 ${noun(to)}`;
    else if (to && ins === 1 && outs >= 2) shape = ` · 1 ${noun(flow.from)} → ${count(outs)} ${noun(to, outs)}`;
  }
  const name = cap(stripSystem(flow.process));
  const text = `${name}: ${kindWord}, on time${shape}`;
  const out = run ? [...run].sort((a, b) => b.at - a.at).find((d) => d.status && d.status !== 'NEW') ?? run[0] : undefined;
  return {
    key: `ontime:${flow.process}`, at: flow.last, pinned: false, kind: 'run', tone: 'ok', parts: partsOf(text, null), subject: null,
    open: out ? { kind: 'document', ref: ref(out) } : searchOpen(flow.process), about: { kind: 'integration', process: flow.process, phrase: name },
  };
}

// ---------------------------------------------------------------- the feed

export function feedLines({ activity: a, failed, now }: FeedInput): FeedLine[] {
  const flows = new Map(a.flows.map((f) => [f.process, f]));
  const systemOfCls = new Map(a.byClass.map((c) => [c.cls, c.system]));
  const failing = incidentsOf(a, failed);
  const failingSet = new Set(failing.map((f) => f.process));
  const runs = runsOf(a.latest);
  const newestRunOf = (process: string) => runs.find((g) => g.some((d) => d.process === process));

  // What is going on now (failing, overdue) stays on top; everything else is newest first
  const pinned: FeedLine[] = [];
  for (const f of failing) pinned.push(failingLine(f, flows.get(f.process), systemOfCls, now));
  for (const s of a.silent) if (!failingSet.has(s.process)) pinned.push(silentLine(s.process, s.last, s.usualMs, flows.get(s.process), now));
  const onTime: FeedLine[] = [];
  for (const f of a.flows) {
    if (failingSet.has(f.process) || now - f.last > JUST_RAN) continue;
    const r = rhythmOfFlow(f);
    if (!r.scheduled) continue;
    const kindWord = r.kind === 'hourly' ? 'hourly' : r.kind === 'daily' ? 'daily' : r.short;
    onTime.push(onTimeLine(f, kindWord, newestRunOf(f.process)));
  }

  const lines = runs.map((g) => lineOfRun(g, failed));
  return [...pinned, ...[...onTime, ...lines].sort((x, y) => y.at - x.at)];
}
